import { Component, ReactNode } from "react";
import AppContext from "../context/app-context";
import {
  getSinglePathData,
  getSingleSnowmobileSubPathData,
  getSingleSnowMobilePathData,
  getPoiById,
} from "../../utils/api.utls";
import {
  PathCategoryResponse,
  PathGpsCoordinateResponse,
  PathTagResponse,
} from "../../interfaces/backend/path.interface";
import { AppContentContext } from "../../enums/app.enums";
import { FeatureType } from "../../enums/map.enums";
import { SubPathInfo } from "./subpath-info.component";
import { SnowMobilePathInfo } from "./snowmobile-path-info.component";
import { StandardPathInfo } from "./standard-path-info.component";
import { PoiInfo } from "./poi-info.component";
import { PoiResponse } from "../../interfaces/backend/poi.interface";

type PathInfoProps = {
  isStandAlone: boolean;
};

export interface PathInfoState {
  id: string | undefined;
  name: string;
  name_en: string;
  number: string;
  length: number;
  difficulty: string;
  status: string;
  description: string;
  end: string;
  start: string;
  description_en: string;
  pathGpsCoordinates: PathGpsCoordinateResponse[];
  pathCategoryId: number | undefined;
  tags: PathTagResponse[];
  tagsRaw: any[];
  pathCategory?: PathCategoryResponse;
  zone?: string;
  zones?: string[];
  isVisible: boolean;
  startY: number;
  deltaY: number;
  startHeight: number;
  isDragging: boolean;
  icon?: string;

  poi?: PoiResponse;
}

class PathInfo extends Component<PathInfoProps, PathInfoState> {
  static contextType = AppContext;
  context!: React.ContextType<typeof AppContext>;
  private reversed = false;
  constructor(props: PathInfoProps) {
    super(props);
    this.state = {
      id: undefined,
      name: "",
      name_en: "",
      number: "",
      length: 0,
      difficulty: "",
      status: "",
      description: "",
      end: "",
      start: "",
      description_en: "",
      pathGpsCoordinates: [],
      pathCategoryId: undefined,
      tags: [],
      tagsRaw: [],
      pathCategory: undefined,
      isVisible: true,
      startY: 0,
      deltaY: 0,
      startHeight: 0,
      isDragging: false,
      poi: undefined,
    };
  }

  componentDidMount(): void {
    const { renderedInApp, appContentContext, highlightedType, selectedType } =
      this.context;

    const isFra =
      selectedType === AppContentContext.Fra.toString() ||
      highlightedType === FeatureType.SnowmobileFra;
    const shouldRender = this.props.isStandAlone || renderedInApp;
    if (this.context.highlightedId && shouldRender && !isFra) {
      this.fetchData();
      this.show();
    }
  }
  componentDidUpdate(
    prevProps: Readonly<PathInfoProps>,
    prevState: Readonly<PathInfoState>,
    snapshot?: any,
  ): void {
    const { renderedInApp, appContentContext, selectedType, highlightedType } =
      this.context;

    const shouldRender = this.props.isStandAlone || renderedInApp;

    const isFra =
      selectedType === AppContentContext.Fra.toString() ||
      appContentContext === AppContentContext.Fra.toString() ||
      selectedType === AppContentContext.Fra.toString() ||
      highlightedType === FeatureType.SnowmobileFra;

    if (
      this.context.highlightedId !== this.state.id &&
      shouldRender &&
      !isFra
    ) {
      this.fetchData();
      this.show();
    }

    const dragHandle = document.querySelector("#handle");

    if (!renderedInApp) {
      dragHandle?.addEventListener(
        "mousedown",
        this.startDragging as EventListener,
      );
    } else {
      dragHandle?.addEventListener(
        "touchstart",
        this.startTouchDragging as EventListener,
      );
    }
  }

  hide() {
    this.setState({ isVisible: false });
  }

  show() {
    this.setState({ isVisible: true });
  }

  startDragging = (e: MouseEvent) => {
    const bottomSheet = document.querySelector("#sheet") as HTMLElement;
    e.preventDefault();
    this.setState({
      startY: e.pageY,
      startHeight: parseInt(getComputedStyle(bottomSheet).height),
      deltaY: 0,
      isDragging: true,
    });
    document.addEventListener("mousemove", this.drag);
    document.addEventListener("mouseup", this.stopDragging);
  };
  drag = (e: MouseEvent) => {
    if (!this.state.isDragging) return;
    const bottomSheet = document.querySelector("#sheet") as HTMLElement;

    const { startY, startHeight } = this.state;

    const delta = startY - e.clientY;
    const newHeight = startHeight + delta;
    if (parseInt(getComputedStyle(bottomSheet).height) < 500) {
      bottomSheet.style.height = newHeight + "px";
    }
  };

  startTouchDragging = (e: TouchEvent) => {
    const bottomSheet = document.querySelector("#sheet") as HTMLElement;
    e.preventDefault();
    this.setState({
      startY: e.touches[0].pageY,
      startHeight: parseInt(getComputedStyle(bottomSheet).height),
      deltaY: 0,
      isDragging: true,
    });
    document.addEventListener("touchmove", this.dragTouch);
    document.addEventListener("touchend", this.stopDragging);
  };

  dragTouch = (e: TouchEvent) => {
    if (!this.state.isDragging) return;
    const { startY, startHeight } = this.state;

    const bottomSheet = document.querySelector("#sheet") as HTMLElement;
    const delta = startY - e.touches[0].clientY;
    const newHeight = startHeight + delta;
    bottomSheet.style.height = newHeight + "px";
  };

  stopDragging = () => {
    const { renderedInApp } = this.context;
    if (!renderedInApp) {
      document.removeEventListener("mousemove", this.drag);
      document.removeEventListener("mouseup", this.stopDragging);
    } else {
      document.removeEventListener("touchmove", this.dragTouch);
      document.removeEventListener("touchend", this.stopDragging);
    }
    this.setState({ isDragging: false });
  };

  fetchData() {
    const { highlightedId, highlightedType } = this.context;
    const isSnowMobilingSubPath =
      highlightedType === AppContentContext.SnowmobilingSubPath.toString();

    const isSnowMobilingPath =
      highlightedType === AppContentContext.SnowmobilePath.toString();

    const isPoi = highlightedType === FeatureType.Poi;

    const fetch = () => {
      if (isPoi) {
        Promise.resolve(getPoiById(highlightedId as string)).then((poi) => {
          this.setState({
            poi,
          });
        });
      }

      if (isSnowMobilingSubPath) {
        Promise.resolve(
          getSingleSnowmobileSubPathData(highlightedId as string),
        ).then((path) => {
          this.setState({
            name: path.name,
            name_en: path.name_en,
            status: path.status,
            zone: path.zone,
          });
        });
        return;
      }
      if (isSnowMobilingPath && !isSnowMobilingSubPath) {
        Promise.resolve(
          getSingleSnowMobilePathData(highlightedId as string),
        ).then((path) => {
          this.setState({
            name: path.name,
            name_en: path.name_en,
            description: path.description,
            length: path.length,
            zones: path.subPaths?.map((subPath) => subPath.zone),
          });
        });
        return;
      }
      Promise.resolve(getSinglePathData("paths", highlightedId)).then(
        (path) => {
          this.setState({
            name: path.name,
            name_en: path.name_en,
            number: path.number,
            length: path.length,
            difficulty: path.difficulty,
            description: path.description,
            description_en: path.description_en,
            end: path.end,
            start: path.start,
            pathGpsCoordinates: path.pathGpsCoordinates,
            pathCategoryId: path.pathCategoryId,
            tags: path.tags,
            tagsRaw: path.tagsRaw,
            pathCategory: path.pathCategory,
            status: path.status,
          });
        },
      );
    };

    if (highlightedId) {
      fetch();
    }
    this.setState({ id: this.context.highlightedId });
  }

  render(): ReactNode {
    if (!this.state.isVisible) return null;
    const {
      mapPathCategories,
      highlightedType,
      highlightedId,
      selectedType,
      selectedId,
      appContentContext,
      showScooterInfo,
      useEnglish,
    } = this.context;

    const showScooterWindows = showScooterInfo;

    const isSnowMobilingSubPath =
      selectedType === AppContentContext.SnowmobilingSubPath.toString() ||
      highlightedType === AppContentContext.SnowmobilingSubPath.toString();

    const isSnowMobilingPath =
      selectedType === AppContentContext.SnowmobilePath.toString() ||
      highlightedType === AppContentContext.SnowmobilePath.toString();

    const isStandAlone = this.props.isStandAlone && window.innerWidth > 768;

    const isPoi = highlightedType === FeatureType.Poi;

    if (!highlightedId) {
      return <></>;
    }

    if (isPoi && this.state.poi)
      return (
        <PoiInfo
          data={this.state.poi}
          onClose={() => this.hide()}
          isStandAlone={this.props.isStandAlone}
        />
      );

    if (
      isSnowMobilingPath ||
      appContentContext === AppContentContext.SnowmobileTours
    ) {
      const { name, name_en, description, zones, description_en } = this.state;
      return (
        <SnowMobilePathInfo
          id={highlightedId}
          onClose={() => this.hide()}
          name={useEnglish ? name_en : name}
          zones={zones || []}
          description={useEnglish ? description_en : description}
          isStandAlone={this.props.isStandAlone}
          isTour={highlightedType === AppContentContext.SnowmobilePath.toString() || appContentContext === AppContentContext.SnowmobileTours}
        />
      );
    }

    if (isSnowMobilingSubPath) {
      return (
        showScooterWindows && (
          <SubPathInfo
            isStandAlone={this.props.isStandAlone}
            name={this.state.name}
            zone={this.state.zone as string}
            status={""}
            onClose={() => this.hide()}
          />
        )
      );
    }
    if (highlightedId && highlightedType && this.state.pathCategoryId) {
      return (
        <StandardPathInfo
          state={{ ...this.state }}
          isStandAlone={isStandAlone}
          mapPathCategories={mapPathCategories}
          onClose={() => this.hide()}
        />
      );
    } else {
      return <></>;
    }
  }
}

export default PathInfo;
